/**
 * Regras do admin "Redirector / Monetização" como funções puras (testáveis sem DOM).
 *
 * Espelham o backend (`redirect_interstitial.normalizar` e `server._ads_saneado`): o servidor
 * continua sendo quem decide; aqui é só para o admin ver o mesmo resultado antes de salvar.
 */

export const ORIGENS = [
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'telegram', label: 'Telegram' },
    { key: 'site', label: 'Site' },
    { key: 'pwa', label: 'App/PWA' },
    { key: 'banner', label: 'Banner' },
    { key: 'unknown', label: 'Outras' },
];

export const LOJAS = [
    { key: 'mercadolivre', label: 'Mercado Livre' },
    { key: 'shopee', label: 'Shopee' },
    { key: 'amazon', label: 'Amazon' },
];

export const PROVEDORES = [
    { key: 'none', label: 'Sem anúncio' },
    { key: 'house', label: 'Banner Super Vitrine' },
    { key: 'sponsor', label: 'Patrocinado' },
    { key: 'google_adsense', label: 'Google AdSense (estrutura; não ativar ainda)' },
];

export const VIEWPORTS = [
    { key: '320x568', w: 320, h: 568 },
    { key: '360x640', w: 360, h: 640 },
    { key: '390x844', w: 390, h: 844 },
    { key: '430x932', w: 430, h: 932 },
    { key: 'desktop', w: 1024, h: 768 },
];

// Padrão = OFF e sem origem ligada. Ativar é sempre um ato explícito do admin.
export const CONFIG_PADRAO = {
    enabled: false,
    provider: 'none',
    cta_text: 'Ir para a oferta',
    auto_redirect: false,
    auto_redirect_seconds: 5,
    sources: { whatsapp: false, telegram: false, site: false },
    marketplaces: {},
    google_slot: '',
    google_responsive: true,
    google_device: 'all',
};

/** Mesma regra do servidor: Google força auto_redirect OFF; segundos entre 3 e 30; slot só dígitos. */
export const normalizaConfig = (c) => {
    const cfg = { ...CONFIG_PADRAO, ...(c || {}) };
    cfg.enabled = cfg.enabled === true;
    cfg.provider = PROVEDORES.some((p) => p.key === cfg.provider) ? cfg.provider : 'none';
    const seg = parseInt(cfg.auto_redirect_seconds, 10);
    cfg.auto_redirect_seconds = Math.min(30, Math.max(3, Number.isFinite(seg) ? seg : 5));
    cfg.auto_redirect = cfg.auto_redirect === true && cfg.provider !== 'google_adsense';
    cfg.google_slot = /^\d{6,12}$/.test(String(cfg.google_slot || '')) ? String(cfg.google_slot) : '';
    cfg.cta_text = String(cfg.cta_text || '').replace(/\s+/g, ' ').trim().slice(0, 40) || 'Ir para a oferta';
    return cfg;
};

/** "—" quando não há base; nunca porcentagem inventada. Sempre com N. */
export const taxa = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed(1)}%` : '—');

export const somaMetricas = (rows) => (rows || []).reduce((acc, r) => {
    ['page_view', 'offer_continue_click', 'affiliate_redirect', 'ad_impression', 'ad_click'].forEach((k) => {
        acc[k] = (acc[k] || 0) + (r[k] || 0);
    });
    return acc;
}, {});

const TEXTO_PROIBIDO = /<|>|javascript:|vbscript:|data:|\bon[a-z]+\s*=/i;

/** https sem credenciais, ou rota interna ("/x", nunca "//host"). */
export const urlSegura = (url) => {
    const u = String(url || '').trim();
    if (!u) return true;
    if (u.startsWith('/') && !u.startsWith('//')) return true;
    try {
        const p = new URL(u);
        return p.protocol === 'https:' && !p.username && !p.password;
    } catch {
        return false;
    }
};

/** Erros do anúncio antes de enviar (o servidor valida de novo). */
export const errosAnuncio = (ad) => {
    const erros = [];
    ['name', 'custom_title', 'subtitle', 'custom_cta', 'advertiser'].forEach((c) => {
        if (TEXTO_PROIBIDO.test(String(ad[c] || ''))) erros.push(`${c}: sem HTML, script ou handler`);
    });
    ['custom_link', 'custom_image', 'custom_image_mobile'].forEach((c) => {
        if (!urlSegura(ad[c])) erros.push(`${c}: use https ou rota interna`);
    });
    if (ad.google_slot && !/^\d{6,12}$/.test(String(ad.google_slot))) erros.push('google_slot: só números');
    if (ad.position === 'redirect_interstitial') {
        if (!['house', 'sponsor'].includes(ad.redirect_kind)) erros.push('tipo: house ou sponsor');
        if (!ad.custom_link) erros.push('custom_link: obrigatório');
        if (ad.redirect_kind === 'house' && ad.custom_link && !ad.custom_link.startsWith('/')
            && !/^https:\/\/(www\.)?supervitrineofertas\.com(\/|$)/.test(ad.custom_link)) {
            erros.push('house ad só aponta para o Super Vitrine');
        }
    }
    return erros;
};

// Campos que o formulário envia. `custom_html` NÃO existe mais: HTML livre foi desligado (stored XSS).
export const CAMPOS_ANUNCIO = ['name', 'type', 'status', 'position', 'device', 'priority', 'start_date', 'end_date',
    'google_slot', 'google_client', 'custom_image', 'custom_image_mobile', 'custom_title', 'custom_link', 'custom_cta',
    'redirect_kind', 'subtitle', 'advertiser', 'sources', 'marketplaces'];

export const corpoAnuncio = (ad) => CAMPOS_ANUNCIO.reduce((o, k) => (k in ad ? { ...o, [k]: ad[k] } : o), {});
